type Mark = { type?: string; attrs?: { href?: string } };

type Node = {
  type?: string;
  text?: string;
  attrs?: { level?: number; href?: string };
  marks?: Mark[];
  content?: Node[];
};

function parts(node: Node | null | undefined): string {
  if (!node) return "";
  if (node.type === "text") return node.text || "";
  return (node.content || []).map(parts).join("");
}

export function docToMarkdown(body: unknown): string {
  const doc = body as Node;
  return (doc?.content || []).map(block).filter(Boolean).join("\n\n");
}

function block(node: Node): string {
  const inner = (node.content || []).map((child) => (
    child.type === "paragraph" || child.type === "heading" ? parts(child) : block(child)
  )).join("");
  switch (node.type) {
    case "heading":
      return `${"#".repeat(node.attrs?.level || 1)} ${parts(node)}`;
    case "bulletList":
      return (node.content || []).map((item) => `- ${parts(item)}`).join("\n");
    case "orderedList":
      return (node.content || []).map((item, i) => `${i + 1}. ${parts(item)}`).join("\n");
    case "codeBlock":
      return `\`\`\`\n${parts(node)}\n\`\`\``;
    case "blockquote":
      return parts(node).split("\n").map((line) => `> ${line}`).join("\n");
    default:
      return inner || parts(node);
  }
}

export function wordCount(body: unknown): number {
  const text = parts(body as Node).trim();
  if (!text) return 0;
  return text.split(/\s+/).length;
}

export function charCount(body: unknown): number {
  return parts(body as Node).length;
}

/** Unique http(s) links from TipTap marks / attrs. */
export function extractLinks(body: unknown): Array<{ href: string; text: string }> {
  const out: Array<{ href: string; text: string }> = [];
  const seen = new Set<string>();

  function walk(node: Node | null | undefined) {
    if (!node) return;
    if (node.type === "text" && node.text) {
      for (const mark of node.marks || []) {
        if (mark.type !== "link") continue;
        const href = String(mark.attrs?.href || "").trim();
        if (!href || seen.has(href)) continue;
        seen.add(href);
        out.push({ href, text: node.text.trim() || href });
      }
    }
    const href = String(node.attrs?.href || "").trim();
    if (href && (node.type === "link" || /^https?:\/\//i.test(href)) && !seen.has(href)) {
      seen.add(href);
      out.push({ href, text: parts(node).trim() || href });
    }
    for (const child of node.content || []) walk(child);
  }

  walk(body as Node);
  return out;
}

export function headings(body: unknown): Array<{ level: number; text: string }> {
  const doc = body as Node;
  const out: Array<{ level: number; text: string }> = [];
  for (const node of doc?.content || []) {
    if (node.type !== "heading") continue;
    const text = parts(node).trim();
    if (!text) continue;
    out.push({ level: node.attrs?.level || 1, text });
  }
  return out;
}
